import { FILES_URLS_HEADER } from '@flashka/shared/storage/files-worker';

import { hc } from 'hono/client';
import { AppType } from 'server';

import { clearJwtCache, getJwt } from '@/auth/better/client';
import { getWebApiOrigin } from '@/utils/dev-origin';

/**
 * Every RPC call carries the Better Auth JWT explicitly, verified against the
 * JWKS by server/utils/auth.ts.
 *
 * The first-party session cookie (server/routes/auth-proxy.ts) would authenticate
 * these too, but verifying it costs one `get-session` subrequest to the auth
 * worker per request — `cookieCache` is disabled there, so it cannot be checked
 * locally. This is the app's chattiest path, so it pays for the JWT; the one-shot
 * raw `fetch` call sites ride the cookie instead (see auth/better/ambient-auth-fetch.ts).
 */
const betterAuthFetch: typeof fetch = async (input, init) => {
  const headers = new Headers(init?.headers);
  // Storage URLs from files.flashka.ai rather than R2: see FILES_URLS_HEADER.
  headers.set(FILES_URLS_HEADER, '1');
  // An explicit Authorization on the call wins, and its 401 is not ours to heal.
  const jwt = headers.has('Authorization') ? null : await getJwt();
  const res = await send(jwt, headers, input, init);
  // A 401 on a request that DID carry our JWT is about the token, not the
  // caller: the session behind it ended, or the auth worker rotated its signing
  // key while a 15-min token was still inside its TTL. Either way the cache
  // stays wrong until the token expires — and it is what every later RPC on
  // this tab reads — so drop it and mint once. Replaying is safe precisely
  // because it was a 401: the server rejected before doing any work, and `hc`
  // builds plain string/FormData bodies, so `init` is re-sendable.
  if (res.status !== 401 || !jwt) return res;
  clearJwtCache();
  const fresh = await getJwt();
  // No new token, or the same one back: nothing changed, so the 401 stands.
  if (!fresh || fresh === jwt) return res;
  return send(fresh, headers, input, init);
};

function send(
  jwt: string | null,
  headers: Headers,
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  if (jwt) headers.set('Authorization', `Bearer ${jwt}`);
  return fetch(input, { ...init, headers });
}

export const clientRPC = hc<AppType>(getWebApiOrigin(), { fetch: betterAuthFetch });
